import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, logout } from '../lib/auth-client';
import { useSession } from '../contexts/SessionContext';
// Trilha 3 Metade B — escrita cifrada real (substitui o placeholder base64). A
// fundamentação e a carta ao denunciante são cifradas no navegador com a DEK do
// caso (AAD=caseId). A carta ao denunciado segue em claro POR DESIGN (sem PII do
// denunciante). Gateado pela leitura: sem material a decisão NÃO é emitida.
import { resolveCaseDekAccess, encryptCaseFields, type CaseDekAccess } from '../lib/case-crypto';
import { kekMortaNestaSessao } from '../lib/kek-member-session';
import { Alert, Button, Card, Field, Textarea } from '@aions/ui-primitives';

type Kind = 'procedente' | 'improcedente' | 'improcedente_insuficiencia';

export function DecisaoPage({
  caseId,
  onBack,
  onDone,
}: {
  caseId: string;
  onBack: () => void;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { session, setSession } = useSession();
  const [kind, setKind] = useState<Kind>('improcedente');
  const [justificativa, setJustificativa] = useState('');
  const [carta, setCarta] = useState('');
  const [denunciado, setDenunciado] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Código do bloqueio da IA-Auditora (auditoraReason do 422), mapeado p/ texto
  // na renderização do erro — interpola o {{reason}} de decisao.errors.auditora_block.
  const [auditoraReason, setAuditoraReason] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ prazoFim: number } | null>(null);
  // null = carregando o material do caso (GET /cases/:id no mount, A-opt-1).
  const [access, setAccess] = useState<CaseDekAccess | null>(null);
  // Regra 1 — a KEK morreu nesta sessão (F5 / descarte de aba). Detectado no MOUNT,
  // ANTES de o membro digitar: descobrir só no clique do "emitir" custa o
  // retrabalho inteiro (ele redige tudo de novo e só então leva erro).
  const [sessaoExpirada, setSessaoExpirada] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let sondar = false;
    void (async () => {
      try {
        const resp = await apiFetch(`/api/comite/cases/${caseId}`);
        if (!resp.ok) {
          if (!cancelled) setAccess({ status: 'sem_keypair' });
          return;
        }
        const json = (await resp.json()) as {
          myWrappedPrivateKey?: { ciphertext: string; nonce: string } | null;
          dekWrappedByMembers?: Array<{ memberId: string; wrappedDek: string }> | null;
        };
        const resolvido = resolveCaseDekAccess(json, session?.user.id ?? '');
        if (cancelled) return;
        setAccess(resolvido);
        sondar = resolvido.status === 'ok';
      } catch {
        if (!cancelled) setAccess({ status: 'sem_keypair' });
      }
      // SONDA da KEK (regra 1) — FORA do try acima, de propósito. Dentro dele, uma
      // exceção da sonda viraria `sem_keypair`, que é DIAGNÓSTICO ERRADO:
      // sem_keypair significa "o membro não tem chave nenhuma", não "a sonda
      // falhou". O try segue cobrindo exatamente o que sempre cobriu (o GET e a
      // resolução do acesso). `kekMortaNestaSessao` nunca lança e só bloqueia com
      // determinação positiva — aviso antecipado, não gate.
      if (sondar && !cancelled) {
        const morta = await kekMortaNestaSessao();
        if (!cancelled && morta) setSessaoExpirada(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, session]);

  const leituraIndisponivel = access !== null && access.status !== 'ok';

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!access || access.status !== 'ok') {
      setError('leitura_indisponivel');
      return;
    }
    setSubmitting(true);
    setError(null);
    setAuditoraReason(null);
    try {
      // Cifra real: fundamentação + carta ao denunciante (AAD=caseId). NUNCA placeholder.
      // A carta ao denunciado vai em claro por design (sem PII do denunciante).
      let enc;
      try {
        enc = await encryptCaseFields(access, caseId, { justificativa, carta });
      } catch {
        setError('encrypt_failed');
        return;
      }
      const resp = await apiFetch(`/api/comite/cases/${caseId}/decisao`, {
        method: 'POST',
        body: JSON.stringify({
          kind,
          justificativaCiphertext: enc.justificativa.ciphertext,
          nonce: enc.justificativa.nonce,
          cartaCiphertextDenunciante: enc.carta.ciphertext,
          cartaNonceDenunciante: enc.carta.nonce,
          cartaParaDenunciado: denunciado,
          votoTally: { procedente: 0, improcedente: 0, improcedente_insuficiencia: 0 },
        }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as {
          error?: string;
          auditoraReason?: string;
        };
        setError(body.error ?? 'Erro ao emitir decisão.');
        setAuditoraReason(body.auditoraReason ?? null);
        return;
      }
      const data = (await resp.json()) as { prazoRecursalAteEpochMs: number };
      setSuccess({ prazoFim: data.prazoRecursalAteEpochMs });
    } catch {
      setError('network');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    const date = new Date(success.prazoFim).toLocaleDateString('pt-BR');
    return (
      <div>
        <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 16 }}>
          ← {t('case_detail.back')}
        </Button>
        <Card>
          <Alert variant="success" style={{ marginBottom: 16 }}>
            Decisão emitida com sucesso. Prazo recursal aberto até <strong>{date}</strong>.
          </Alert>
          <Button onClick={onDone}>Voltar ao caso</Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 16 }}>
        ← {t('case_detail.back')}
      </Button>
      <Card style={{ maxWidth: 800 }}>
        <h2>{t('decisao.title')}</h2>
        {sessaoExpirada && (
          <Alert
            variant="warning"
            role="status"
            aria-live="polite"
            title={t('case_detail.sessao_expirada_title')}
            style={{ marginBottom: 16 }}
          >
            <div>{t('case_detail.sessao_expirada_body')}</div>
            {/* Incondicional, e é uma DECLARAÇÃO DE CONSEQUÊNCIA, não instrução.
                "Copie o texto" assumiria que existe texto — condição que pode errar
                (a sonda é assíncrona e o campo fica ativo até ela responder: há
                janela real para digitar antes do veredito, provado nos testes
                `sonda-tardia`). "Descarta qualquer texto não enviado" é verdade com
                ou sem rascunho, então nunca mente. */}
            <div style={{ marginTop: 6 }}>{t('case_detail.sessao_expirada_descarte')}</div>
            <div style={{ marginTop: 10 }}>
              <Button
                variant="primary"
                size="sm"
                data-testid="sessao-expirada-entrar"
                onClick={() => {
                  void (async () => {
                    await logout();
                    setSession(null);
                  })();
                }}
              >
                {t('case_detail.sessao_expirada_acao')}
              </Button>
            </div>
          </Alert>
        )}
        {leituraIndisponivel && (
          <Alert variant="warning" role="status" style={{ marginBottom: 16 }}>
            {t('decisao.leitura_indisponivel_aviso')}
          </Alert>
        )}
        {error && (
          <Alert variant="error" style={{ marginBottom: 16 }}>
            {/* Mapeia auditoraReason → texto (reusa as chaves do banner IA-Auditora)
                p/ interpolar o {{reason}} de decisao.errors.auditora_block. */}
            {t(`decisao.errors.${error}`, {
              defaultValue: error,
              reason:
                auditoraReason === 'oitiva_ausente_e_dispensa_nao_justificada'
                  ? t('ia_auditora.banner.reason_oitiva_ausente')
                  : auditoraReason === 'conflito_de_interesse_pendente'
                    ? t('ia_auditora.banner.reason_conflito_pendente')
                    : (auditoraReason ?? t('ia_auditora.banner.block_description')),
            })}
          </Alert>
        )}
        <form onSubmit={(e) => void handleSubmit(e)}>
          {/* Grupo de rádios do resultado: label de grupo via cf-field__label. */}
          <div className="cf-field" role="radiogroup" aria-label={t('decisao.kind_label')}>
            <span className="cf-field__label">{t('decisao.kind_label')}</span>
            {(['procedente', 'improcedente', 'improcedente_insuficiencia'] as const).map((k) => (
              <label
                key={k}
                style={{ display: 'flex', gap: 10, padding: '8px 0', cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  name="kind"
                  value={k}
                  checked={kind === k}
                  onChange={() => setKind(k)}
                />
                <span>{t(`parecer.votos.${k}`)}</span>
              </label>
            ))}
          </div>

          <Field label={t('decisao.justificativa_label')}>
            {({ id }) => (
              <Textarea
                id={id}
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                required
              />
            )}
          </Field>

          <Field label={t('decisao.carta_label')} help={t('decisao.carta_help')}>
            {({ id, describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                value={carta}
                onChange={(e) => setCarta(e.target.value)}
                required
              />
            )}
          </Field>

          <Field label={t('decisao.denunciado_label')}>
            {({ id }) => (
              <Textarea
                id={id}
                value={denunciado}
                onChange={(e) => setDenunciado(e.target.value)}
                required
              />
            )}
          </Field>

          <Button
            type="submit"
            loading={submitting}
            disabled={access === null || leituraIndisponivel || sessaoExpirada}
          >
            {t('decisao.submit')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
